<template>
  <Carousel
    ref="carouselRef"
    class="md:hidden w-full"
    :opts="{
      align: 'center',
      loop: false,
    }"
    @init-api="onInitApi"
  >
    <CarouselContent>
      <CarouselItem
        v-for="(tab, index) in tabs"
        :key="tab.title"
        :class="getTabSliderClass(index, tabs.length) + ' flex justify-center basis-auto w-3/5'"
      >
        <Button
          class="rounded-full w-full"
          size="lg"
          @click="selectTab(tab.title, index)"
          :variant="activeTab === tab.title ? 'secondary' : 'greyed'"
        >
          {{ tab.title }}
        </Button>
      </CarouselItem>
    </CarouselContent>
    <CarouselDotButtons class="mt-4 lg:hidden" selected-class="border-primary" />
  </Carousel>
  <Tabs v-model="activeTab">
    <TabsList
      class="hidden md:inline-flex items-center justify-between w-full rounded-full h-12 border-grey-dark-900 border bg-grey-dark-900"
    >
      <TabsTrigger
        v-for="tab in tabs"
        :key="tab.title"
        :value="tab.title"
        class="rounded-full text-sm data-[state=active]:bg-grey-dark-950 text-grey-light-500 data-[state=active]:text-white cursor-pointer"
      >
        {{ tab.title }}
      </TabsTrigger>
    </TabsList>
    <TabsContent v-for="tab in tabs" :key="tab.title" :value="tab.title" class="mt-6 w-[95%] mx-auto">
      <TitleFeaturesSection :features="processFeatures(tab.info)" :title="tab.heading" :description="tab.description" />
    </TabsContent>
  </Tabs>
</template>

<script setup lang="ts">
// Import solution images - you'll need to add these to your assets folder
import dashpaySolution from '@/assets/images/business/solutionsTab/dashpaySolution.svg';
import magentoSolution from '@/assets/images/business/solutionsTab/magentoSolution.svg';
import shopifySolution from '@/assets/images/business/solutionsTab/shopifySolution.svg';
import wooCommerceSolution from '@/assets/images/business/solutionsTab/wooCommerceSolution.svg';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselDotButtons,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTabSliderClass } from '@/utils/slider';
import { ref } from 'vue';
import TitleFeaturesSection from './business/TitleFeaturesSection.vue';

export interface InfoBlock {
  icon?: string;
  title?: string;
  description?: string;
  image?: string;
  alt?: string;
}

export interface FeaturesTab {
  title: string;
  heading: string;
  description: string;
  info: InfoBlock[];
}

interface Props {
  tabs: FeaturesTab[];
}

const props = defineProps<Props>();

const activeTab = ref(props.tabs[0]?.title);
const carouselApi = ref<CarouselApi>();

// Map image names to imported images
const imageMap: Record<string, string> = {
  wooCommerceSolution,
  shopifySolution,
  magentoSolution,
  dashpaySolution,
};

const processFeatures = (features: InfoBlock[]) => {
  return features.map((feature) => {
    if (feature.image && imageMap[feature.image]) {
      return {
        ...feature,
        image: imageMap[feature.image],
      };
    }
    return feature;
  });
};

const onInitApi = (api: CarouselApi) => {
  carouselApi.value = api;
};

const selectTab = (title: string, index: number) => {
  activeTab.value = title;
  carouselApi.value?.scrollTo(index, false);
};
</script>

<style scoped>
[data-state='open'] .icon-closed {
  display: none;
}

[data-state='closed'] .icon-open {
  display: none;
}
</style>
